import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSEO } from '@/lib/seo';
import { IDS } from '@/constants/testIds';
import { APP } from '@/constants/brand';
import { Wrench, Loader2, ArrowLeft } from 'lucide-react';

export default function AuthPage() {
  const [params] = useSearchParams();
  const initialMode = params.get('mode') === 'signup' ? 'signup' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup, user } = useAuth();
  const navigate = useNavigate();

  useSEO(
    mode === 'signup'
      ? {
          title: 'Sign Up — DIY X-treme | Create your free repair account',
          description: 'Create a free DIY X-treme account to get AI-powered repair guides, tool lists, safety warnings, and personalized help from Corey or Christine. Every trade. Every fix.',
          canonical: 'https://diybot.org/auth?mode=signup',
        }
      : {
          title: 'Log In — DIY X-treme | Continue your repair',
          description: 'Log into DIY X-treme to continue your fix, review past repairs, and access your saved nameplates.',
          canonical: 'https://diybot.org/auth?mode=login',
        }
  );

  useEffect(() => {
    if (!user) return;
    // If they came via a LandingPage buddy card (?guide=…), preserve that into
    // the /app URL so the picker can auto-trigger the pick+intro flow.
    const preGuide = params.get('guide');
    const dest = (preGuide === 'corey' || preGuide === 'christine')
      ? `/app?autoBuddy=${preGuide}`
      : '/app';
    navigate(dest, { replace: true });
  }, [user, navigate, params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        await signup(email.trim(), password, name.trim() || email.split('@')[0]);
      } else {
        await login(email.trim(), password);
      }
      // Navigation is handled by the user-watching useEffect above, which honors
      // the ?guide= URL param so pre-selected buddy flows land on /app?autoBuddy=…
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 grid-bg relative">
      <button
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
        data-testid="auth-back-btn"
        className="absolute top-4 left-4 inline-flex items-center gap-1 text-zinc-400 hover:text-amber-500 border border-zinc-800 hover:border-amber-500 px-3 py-2 font-mono text-xs uppercase tracking-widest bg-zinc-950/70 backdrop-blur transition-colors z-10"
        title="Back"
        aria-label="Back"
      >
        <ArrowLeft size={14} />
        <span>Back</span>
      </button>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-12 h-12 bg-amber-500 flex items-center justify-center">
            <Wrench size={26} className="text-zinc-950" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-zinc-100 tracking-wide leading-none">{APP.name}</h1>
            <div className="font-mono text-[10px] text-amber-500 uppercase tracking-[0.2em] mt-1">
              {APP.tagline}
            </div>
          </div>
        </div>

        <h2 className="sr-only">
          {mode === 'signup' ? 'Create your DIY X-treme account' : 'Log in to DIY X-treme'}
        </h2>

        <div className="industrial-card border-t-2 border-t-amber-500">
          <div className="flex border-b border-zinc-800 -mx-6 -mt-6 mb-6">
            <button
              data-testid={IDS.authTabLogin}
              onClick={() => setMode('login')}
              className={`flex-1 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                mode === 'login' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-zinc-500'
              }`}
            >
              Log In
            </button>
            <button
              data-testid={IDS.authTabSignup}
              onClick={() => setMode('signup')}
              className={`flex-1 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                mode === 'signup' ? 'text-amber-500 border-b-2 border-amber-500' : 'text-zinc-500'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                  Name
                </label>
                <input
                  data-testid={IDS.authName}
                  type="text"
                  className="industrial-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
            )}
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
                Email
              </label>
              <input
                data-testid={IDS.authEmail}
                type="email"
                className="industrial-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block font-mono text-xs uppercase tracking-widest text-zinc-500">
                  Password
                </label>
                {mode === 'login' && (
                  <Link
                    to="/auth/forgot"
                    data-testid="auth-forgot-link"
                    className="font-mono text-[10px] uppercase tracking-widest text-amber-400 hover:text-amber-300"
                  >
                    Forgot?
                  </Link>
                )}
              </div>
              <input
                data-testid={IDS.authPassword}
                type="password"
                className="industrial-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min 6 characters"
                minLength={6}
                required
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            </div>

            {error && (
              <div
                data-testid={IDS.authError}
                className="border border-red-500/40 bg-red-500/10 text-red-400 px-3 py-2 text-sm font-mono"
              >
                {error}
              </div>
            )}

            <button
              data-testid={IDS.authSubmit}
              type="submit"
              className="btn-primary w-full mt-2"
              disabled={loading}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : mode === 'signup' ? 'Create Account' : 'Log In'}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 text-zinc-500 text-sm font-mono">
          {mode === 'login' ? 'New here?' : 'Already have an account?'}{' '}
          <button
            onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
            className="text-amber-500 hover:underline"
          >
            {mode === 'login' ? 'Sign up' : 'Log in'}
          </button>
        </div>
      </div>
    </div>
  );
}
